import React, { useRef, useEffect, useState } from "react";
import * as echarts from "echarts";
import {
  normalizeTimestampToMs,
  formatAxisLabelEvolution,
} from "../../helpers/widgetsCharts";

const Chart2 = ({
  dataChart,
  unit,
  heightPx,
  deviceNameByMac = {},
  isWidget = false,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const rafId = useRef(null);

  const [viewMode, setViewMode] = useState("total");

  const deviceCount = dataChart?.length || 0;

  const measureMapping = {
    corriente: ["R_corriente", "S_corriente", "T_corriente"],
    tension: ["R_tension", "S_tension", "T_tension"],
    potActiva: ["R_pActiva", "S_pActiva", "T_pActiva"],
    potReactiva: ["R_pReactiva", "S_pReactiva", "T_pReactiva"],
    eActiva: ["R_eActiva", "S_eActiva", "T_eActiva"],
    eReactiva: ["R_eReactiva", "S_eReactiva", "T_eReactiva"],
  };

  const totalKey = "total_valor";
  const defaultKeys = ["R_valor", "S_valor", "T_valor"];

  const palette = [
    "#3498db",
    "#f39c12",
    "#2ca02c",
    "#9b59b6",
    "#e74c3c",
    "#2ecc71",
  ];

  const phaseTone = [0.25, 0, -0.25];

  const getDeviceLabel = (device) => {
    const mac = device?.mac;
    return (mac && deviceNameByMac?.[mac]) || mac || "Dispositivo";
  };

  useEffect(() => {
    if (!chartRef.current || !dataChart?.length) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    const myChart = echarts.init(chartRef.current);
    chartInstance.current = myChart;

    const series = [];
    const legendData = new Set();

    dataChart.forEach((device, deviceIndex) => {
      const mac = device.mac;
      const deviceLabel = getDeviceLabel(device);
      const baseColor = palette[deviceIndex % palette.length];

      const measureType = Object.keys(device).find((k) =>
        Object.keys(measureMapping).includes(k),
      );

      const rawRows = measureType ? device[measureType] : device.data;

      if (!Array.isArray(rawRows) || !rawRows.length) return;

      const rows = rawRows
        .filter((d) => d?.mac === mac)
        .map((d) => {
          const ts = normalizeTimestampToMs(d.timestamp);
          return { ...d, ts };
        })
        .filter((d) => Number.isFinite(d.ts))
        .sort((a, b) => a.ts - b.ts);

      if (!rows.length) return;

      const keys = measureMapping[measureType] || defaultKeys;

      // ===== TOTAL MODE =====
      if (viewMode === "total" || deviceCount >= 4) {
        const name = deviceLabel;
        legendData.add(name);
        const barWidth = rows.length > 200 ? 6 : rows.length > 100 ? 10 : 18;

        series.push({
          name,
          type: "bar",
          stack: deviceCount > 1 ? "total" : undefined,
          barMaxWidth: barWidth,
          data: rows.map((d) => {
            if (d[totalKey] != null) return [d.ts, d[totalKey]];
            const sum = keys.reduce((a, k) => a + (Number(d[k]) || 0), 0);
            return [d.ts, sum];
          }),
          itemStyle: {
            color: baseColor,
            borderRadius: rows.length > 150 ? 0 : [3, 3, 0, 0],
          },
        });

        return;
      }

      // ===== PHASE MODE =====
      keys.forEach((key, i) => {
        const phase = key.startsWith("R_")
          ? "R"
          : key.startsWith("S_")
            ? "S"
            : key.startsWith("T_")
              ? "T"
              : key;

        const color = echarts.color.lift(
          baseColor,
          phaseTone[i % phaseTone.length],
        );

        const name = `${deviceLabel} - Fase ${phase}`;
        legendData.add(name);

        series.push({
          name,
          type: "bar",
          stack: deviceLabel,
          barMaxWidth: 26,
          data: rows.map((d) => [d.ts, d[key] ?? null]),
          itemStyle: {
            color,
            borderRadius: [3, 3, 0, 0],
          },
        });
      });
    });

    const option = {
      animation: false,
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
      },
      grid: {
        left: 30,
        right: 30,
        top: 40,
        bottom: isWidget
          ? legendData.size ? 40 : 20
          : legendData.size ? 110 : 70,
        containLabel: true,
      },
      legend: legendData.size
        ? {
            type: "scroll",
            bottom: 5,
            textStyle: { fontSize: 11 },
            itemHeight: 10,
            itemGap: 12,
          }
        : undefined,
      xAxis: {
        type: "time",
        axisLabel: {
          formatter: (v) => formatAxisLabelEvolution(v),
        },
      },
      yAxis: {
        type: "value",
        name: unit || "",
      },
      dataZoom: isWidget
        ? [
            {
              type: "inside",
              zoomOnMouseWheel: false,
              moveOnMouseWheel: false,
              moveOnMouseMove: false,
            },
          ]
        : [
            {
              type: "inside",
              zoomOnMouseWheel: false,
              moveOnMouseWheel: false,
              moveOnMouseMove: false,
            },
            {
              type: "slider",
              height: 40,
              bottom: 36,
              fillerColor: "rgba(52,152,219,0.15)",
              borderColor: "#dcdcdc",
              handleSize: "120%",
            },
          ],
      toolbox: {
        orient: "horizontal",
        left: "center",
        top: 8,
        feature: {
          dataZoom: {
            yAxisIndex: "none",
          },
          restore: {},
        },
        itemSize: 18,
        itemGap: 12,
        tooltip: {
          show: true,
          backgroundColor: "rgba(0,0,0,0.85)",
          textStyle: { color: "#fff", fontSize: 11 },
          borderRadius: 4,
          padding: [6, 10],
        },
        iconStyle: {
          borderColor: "#999",
        },
      },

      series,
    };

    myChart.setOption(option, true);

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      myChart.dispose();
      chartInstance.current = null;
    };
  }, [dataChart, viewMode, unit, deviceNameByMac]);

return (
  <div
    style={{
      width: "100%",
      height: heightPx || 500,
      display: "flex",
      flexDirection: "column",
    }}
  >
    <div
      style={{
        display: "flex",
        gap: 0,
        marginBottom: 10,
        padding: 3,
        background: "#f0f2f5",
        borderRadius: 6,
        width: "fit-content",
        flexShrink: 0,
      }}
    >
      <button
        onClick={() => setViewMode("phases")}
        style={{
          padding: "6px 14px",
          background: viewMode === "phases" ? "#fff" : "transparent",
          border: "none",
          borderRadius: 4,
          cursor: "pointer",
        }}
      >
        Por fases
      </button>

      <button
        onClick={() => setViewMode("total")}
        style={{
          padding: "6px 14px",
          background: viewMode === "total" ? "#fff" : "transparent",
          border: "none",
          borderRadius: 4,
          cursor: "pointer",
        }}
      >
        Total
      </button>
    </div>

    <div
      style={{
        flex: 1,
        minHeight: 0,
      }}
      ref={chartRef}
    />
  </div>
);

};

export default Chart2;
